import { WishlistButtonWithApi } from '@packages/modules';
import { Stack, Box, Image } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { TaxHint } from '../../TaxHint';
import { Price } from '../../Price';
import { AddToBasketButton } from '../../AddToBasketButton';

const ProductOverviewFragment = graphql(/* GraphQL */ `
  fragment ProductOverviewFragment on Product {
    sku
    name
    price {
      ...ProductPriceFragment
    }
    media {
      type
      url
    }
    delivery {
      deliverySize
    }
    availability {
      state
    }
    ...AddToBasketFragment
  }
`);

type ProductOverviewProps = {
  /**
   * Product data fragment
   */
  productData: FragmentType<typeof ProductOverviewFragment>;
};

/**
 * Product overview component for rating page
 */
export const ProductOverview = ({ productData }: ProductOverviewProps) => {
  const product = getFragmentData(ProductOverviewFragment, productData);
  const {
    media,
    price,
    delivery: { deliverySize },
    sku,
    name,
    availability,
  } = product;

  return (
    <Stack gap={2} flexDirection="row">
      <Box
        sx={{
          width: '100%',
          maxWidth: 196,
          '.ProductOverview-image-wrapper': { position: 'relative', width: '100%', pb: '100%' },
        }}
      >
        <Box className="ProductOverview-image-wrapper">
          <Image
            src={media[0].url}
            style={{
              objectFit: 'contain',
            }}
            fill
            alt=""
            sizes="25vw"
            priority
          />
        </Box>
      </Box>
      <Stack justifyContent="space-between" flexGrow={1}>
        <Stack gap={0.25}>
          <Price priceData={price} />
          <TaxHint deliverySize={deliverySize} />
        </Stack>
        <Stack gap={1}>
          <AddToBasketButton productData={product} />
          <WishlistButtonWithApi
            sku={sku}
            productName={name}
            iconOnly={false}
            availability={availability.state}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
